import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { User, LogOut, Crown, ArrowLeft, Globe, Calendar, Shield } from "lucide-react";

export default function Dashboard() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/", { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#05030A" }}>
        <div className="w-10 h-10 border-2 border-fuchsia-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  const hasSub = user.subscription_status && user.subscription_plan;

  return (
    <div className="min-h-screen relative" style={{ background: "#05030A" }}>
      <div className="noise-overlay" />
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute -top-[30%] -right-[20%] w-[800px] h-[800px] rounded-full bg-[#9D00FF] opacity-[0.04] blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-16">
        <button
          onClick={() => navigate("/")}
          data-testid="dashboard-back-button"
          className="flex items-center gap-2 text-sm text-[#A19BB0] hover:text-white transition-colors duration-200 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to scanner
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* User info */}
          <div className="glass-card rounded-2xl p-8 mb-6" data-testid="user-profile-card">
            <div className="flex items-center gap-4 mb-6">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-14 h-14 rounded-full border-2 border-white/10" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF007F] to-[#9D00FF] flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
              )}
              <div>
                <h2 className="font-['Outfit'] text-xl font-bold text-white">{user.name}</h2>
                <p className="text-sm text-[#A19BB0]">{user.email}</p>
              </div>
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                data-testid="logout-button"
                className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-[#A19BB0] hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors duration-200"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            </div>
          </div>

          {/* Subscription */}
          <div className="glass-card rounded-2xl p-8 mb-6" data-testid="subscription-card">
            <h3 className="font-['Outfit'] text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Crown className="w-5 h-5 text-fuchsia-400" />
              Subscription
            </h3>
            {hasSub ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold tracking-[0.15em] uppercase px-2 py-1 rounded-full bg-green-500/10 text-green-400 border border-green-500/20">
                    {user.subscription_status === "trialing" ? "Trial Active" : "Active"}
                  </span>
                  <span className="text-sm text-white font-medium capitalize">{user.subscription_plan} Plan</span>
                </div>
                {user.trial_ends_at && (
                  <p className="text-sm text-[#A19BB0] flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Trial ends: {new Date(user.trial_ends_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            ) : (
              <div>
                <p className="text-sm text-[#A19BB0] mb-4">No active subscription. Start monitoring your SEO automatically.</p>
                <Link
                  to="/pricing"
                  data-testid="upgrade-button"
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-[#9D00FF] to-[#FF007F] hover:opacity-90 transition-opacity duration-200"
                >
                  View Plans
                </Link>
              </div>
            )}
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link
              to="/"
              data-testid="run-scan-link"
              className="glass-card rounded-xl p-6 flex items-center gap-4 hover:border-white/20 transition-colors duration-200"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FF007F]/20 to-[#9D00FF]/20 flex items-center justify-center">
                <Globe className="w-5 h-5 text-fuchsia-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Run a Scan</p>
                <p className="text-xs text-[#A19BB0]">Audit any website instantly</p>
              </div>
            </Link>
            <div className="glass-card rounded-xl p-6 flex items-center gap-4 opacity-60">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FF007F]/20 to-[#9D00FF]/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-fuchsia-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Monitored Sites</p>
                <p className="text-xs text-[#A19BB0]">Coming soon</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
